import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { playSound } from '../../utils/utils';
import {
  getCollectionRewards,
  getRewardPickups,
  REWARD_FLIGHT_MS,
} from './rewardCollectionTiming';
import './RewardCollection.css';

const center = (element, fallback) => {
  const rect = element?.getBoundingClientRect();
  return rect && (rect.width || rect.height)
    ? { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 }
    : fallback;
};

export const RewardPotion = () => (
  <svg viewBox="0 0 40 48" aria-hidden="true">
    <path
      className="rewardPotionGlass"
      d="M13 5H27V14L34 25V39Q34 45 28 45H12Q6 45 6 39V25L13 14Z"
    />
    <path
      className="rewardPotionLiquid"
      d="M8 27Q14 23 20 27T32 27V39Q32 43 28 43H12Q8 43 8 39Z"
    />
    <path className="rewardPotionCork" d="M13 2H27V9H13Z" />
    <path className="rewardPotionCross" d="M20 29V39M15 34H25" />
  </svg>
);

const RewardCollection = ({
  result,
  originRef,
  healthRef,
  shardsRef,
  delay = 0,
  startAt = 0,
  reduceMotion = false,
  onProgress,
}) => {
  const [flying, setFlying] = useState([]);
  const progressRef = useRef(onProgress);
  progressRef.current = onProgress;

  useEffect(() => {
    const pickups = getRewardPickups(result);
    const rewards = getCollectionRewards(result);
    const collected = {
      result,
      shards: 0,
      healed: 0,
      shardPulse: 0,
      healthPulse: 0,
      healthAmount: 0,
      maxHpAmount: 0,
    };
    progressRef.current?.({ ...collected });
    if (!pickups.length) return undefined;
    const timers = [];
    const destination = (kind) =>
      center(kind === 'shards' ? shardsRef?.current : healthRef?.current, {
        x: window.innerWidth * (kind === 'shards' ? 0.75 : 0.35),
        y: 48,
      });
    const updateDestinations = () =>
      setFlying((current) =>
        current.map((pickup) => ({ ...pickup, end: destination(pickup.kind) }))
      );
    timers.push(
      setTimeout(
        () => {
          if (reduceMotion) {
            progressRef.current?.({
              ...collected,
              shards: rewards.shards,
              healed: rewards.healed,
              shardPulse: rewards.shards > 0 ? 1 : 0,
              healthPulse: rewards.healed > 0 ? 1 : 0,
              healthAmount: rewards.healed,
              maxHpAmount: rewards.maxHp,
            });
            return;
          }
          playSound('/sounds/new-item-collected.mp3', {
            preventOverlap: true,
          });
          const start = center(originRef?.current, {
            x: window.innerWidth / 2,
            y: window.innerHeight * 0.42,
          });
          setFlying(
            pickups.map((pickup, index) => {
              const angle = index * 2.39996;
              const spread = 32 + (index % 4) * 14;
              return {
                ...pickup,
                start,
                scatter: {
                  x: start.x + Math.cos(angle) * spread,
                  y: start.y + Math.sin(angle) * spread - 24,
                },
                end: destination(pickup.kind),
              };
            })
          );
          pickups.forEach((pickup) =>
            timers.push(
              setTimeout(() => {
                if (pickup.kind === 'shards') {
                  playSound('/sounds/collect-shards.mp3', {
                    preventOverlap: true,
                  });
                  collected.shards += pickup.amount;
                  collected.shardPulse += 1;
                } else {
                  playSound('/sounds/rpg-bubble-power-up.mp3', {
                    preventOverlap: true,
                  });
                  const previousMax =
                    rewards.healed > 0
                      ? Math.round(
                          (rewards.maxHp * collected.healed) / rewards.healed
                        )
                      : 0;
                  collected.healed += pickup.amount;
                  collected.healthPulse += 1;
                  collected.healthAmount = pickup.amount;
                  collected.maxHpAmount =
                    rewards.healed > 0
                      ? Math.round(
                          (rewards.maxHp * collected.healed) / rewards.healed
                        ) - previousMax
                      : 0;
                }
                progressRef.current?.({ ...collected });
                setFlying((current) =>
                  current.filter(({ id }) => id !== pickup.id)
                );
              }, pickup.delay + REWARD_FLIGHT_MS)
            )
          );
        },
        startAt ? Math.max(0, startAt - Date.now()) : Math.max(0, delay)
      )
    );
    window.addEventListener('resize', updateDestinations);
    return () => {
      timers.forEach(clearTimeout);
      window.removeEventListener('resize', updateDestinations);
    };
  }, [result, originRef, healthRef, shardsRef, delay, startAt, reduceMotion]);

  if (!flying.length) return null;
  return createPortal(
    <div className="coreboundRewardCollection" aria-hidden="true">
      {flying.map((pickup) => (
        <div
          key={pickup.id}
          className={`coreboundRewardPickup is-${pickup.kind}`}
          data-reward={pickup.kind}
          style={{
            '--pickup-start-x': `${pickup.start.x}px`,
            '--pickup-start-y': `${pickup.start.y}px`,
            '--pickup-scatter-x': `${pickup.scatter.x}px`,
            '--pickup-scatter-y': `${pickup.scatter.y}px`,
            '--pickup-end-x': `${pickup.end.x}px`,
            '--pickup-end-y': `${pickup.end.y}px`,
            animationDelay: `${pickup.delay}ms`,
            animationDuration: `${REWARD_FLIGHT_MS}ms`,
          }}
        >
          <i className="coreboundRewardTrail" />
          {pickup.kind === 'health' ? (
            <RewardPotion />
          ) : (
            <svg viewBox="0 0 32 44">
              <path d="M16 2 29 16 22 37 12 42 3 25 7 10Z" />
              <path d="M16 2 12 20 29 16M12 20 12 42M12 20 3 25M12 20 22 37" />
            </svg>
          )}
          {pickup.amount > 1 && <b>+{pickup.amount}</b>}
        </div>
      ))}
    </div>,
    document.body
  );
};

export default RewardCollection;
